import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard,
    User,
    LogOut,
    Building2,
    Menu,
    X,
    CreditCard
} from 'lucide-react';
import authService from '../services/auth.service';

const AdherentLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const isActiveRoute = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const handleLogout = async () => {
        await authService.logout();
        navigate('/adherent-login');
    };

    const menuItems = [
        {
            category: 'Menu Principal',
            items: [
                {
                    name: 'Tableau de bord',
                    path: '/adherent/dashboard',
                    icon: LayoutDashboard,
                },
                {
                    name: 'Mon Profil',
                    path: '/adherent/profile',
                    icon: User,
                },
                {
                    name: 'Mes Adhésions',
                    path: '/adherent/memberships',
                    icon: CreditCard,
                },
            ],
        },
    ];

    const SidebarContent = () => (
        <>
            {/* Logo & Brand */}
            <div className="p-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center shadow-md">
                        <Building2 className="w-5 h-5 text-white" />
                    </div>
                    {!sidebarCollapsed && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <h1 className="text-base font-semibold text-gray-900">Espace Adhérent</h1>
                            <p className="text-xs text-gray-500">SAPI Algérie</p>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto py-4">
                <nav className="px-3 space-y-6">
                    {menuItems.map((section, sectionIndex) => (
                        <div key={sectionIndex}>
                            {!sidebarCollapsed && (
                                <motion.h3
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2"
                                >
                                    {section.category}
                                </motion.h3>
                            )}
                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const isActive = isActiveRoute(item.path);
                                    const Icon = item.icon;

                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                                                ? 'bg-secondary text-white shadow-sm'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-secondary'
                                                }`}
                                        >
                                            <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-secondary'}`} />
                                            {!sidebarCollapsed && (
                                                <motion.span
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className="ml-3 truncate"
                                                >
                                                    {item.name}
                                                </motion.span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>
            </div>

            {/* Logout Button */}
            <div className="p-4 border-t border-gray-100">
                <button
                    onClick={handleLogout}
                    className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-red-600 hover:bg-red-50`}
                >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    {!sidebarCollapsed && (
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="ml-3 truncate"
                        >
                            Se déconnecter
                        </motion.span>
                    )}
                </button>
            </div>
        </>
    );

    return (
        <div className="flex h-screen bg-white overflow-hidden">
            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 w-full bg-white border-b border-gray-100 z-30 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">Espace Adhérent</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 rounded-md text-gray-600 hover:bg-gray-50"
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile menu backdrop */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 lg:hidden bg-black/40 backdrop-blur-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Desktop Sidebar */}
            <motion.aside
                initial={false}
                animate={{
                    width: sidebarCollapsed ? '80px' : '280px',
                }}
                className="hidden lg:flex bg-white border-r border-gray-100 relative z-30 flex-col"
            >
                <div className="flex flex-col w-full h-full">
                    <SidebarContent />
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="absolute -right-3 top-20 w-6 h-6 bg-secondary text-white rounded-full flex items-center justify-center shadow-md hover:bg-secondary-dark transition-colors"
                    >
                        <motion.div
                            animate={{ rotate: sidebarCollapsed ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            ←
                        </motion.div>
                    </button>
                </div>
            </motion.aside>

            {/* Mobile Sidebar */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.aside
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-2xl lg:hidden"
                    >
                        <div className="flex flex-col h-full pt-16 lg:pt-0">
                            <SidebarContent />
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto bg-white pt-16 lg:pt-0">
                <div className="p-4 lg:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdherentLayout;
